// Toop & Toop detail-page parser. The page's map script embeds the listing
// record (from their Rex CRM) as a single-quoted JSON string:
//   listing:'{"Address":"11A Pier Street","Availability":"Sold",
//     "SoldPrice":1175000,"SoldDate":"2015-11-16T00:00:00","Bedrooms":4,…,
//     "Slug":"11a-pier-street-glenelg-445966"}'
// The record has no state, so the state comes from the page title
// ("11A Pier Street, Glenelg SA 5045 - TOOP+TOOP"). Land size is only in the
// visible facts list ("Land Size</span>  522.00 sqm approx.").
//
// Price, PriceLow and PriceHigh are the campaign guide, never the sale price.
// SoldPrice 0 means the sold price is withheld.

import {
  areaValue,
  count,
  formatAddress,
  htmlText,
  isRecord,
  parseListingDate,
  propertyTypeLabel,
  readTitle,
  stateCode,
  text,
} from "../shared/listing-html.ts";

const LISTING_RE = /\blisting\s*:\s*'(\{.*?\})'\s*[,}\n]/;
const TITLE_RE = /^(.+?),\s*(.+?)\s+([A-Z]{2,3})\s+(\d{4})\s*-/;

function readListing(html: string, url: string): Record<string, unknown> | null {
  const match = html.match(LISTING_RE);
  if (!match) return null;
  let listing: unknown;
  try {
    listing = JSON.parse(match[1].replace(/\\'/g, "'"));
  } catch {
    return null;
  }
  if (!isRecord(listing)) return null;
  // The record's slug is the page path; a different slug is a different listing.
  const path = url
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "")
    .split("/")
    .pop()
    ?.toLowerCase();
  const slug = text(listing.Slug)?.toLowerCase();
  if (path && slug && path !== slug) return null;
  return listing;
}

export function parseToopAndToopDetailHtml(html: string, url: string): Record<string, unknown> {
  const listing = readListing(html, url);
  if (!listing) return {};

  const streetNumber = text(listing.StreetNumber)?.replace(/\s*\/\s*/g, "/");
  const street = text(listing.Street);
  const streetLine = streetNumber && street ? `${streetNumber} ${street}` : text(listing.Address);
  if (!streetLine) return {};
  const suburb = text(listing.Suburb);
  const postcode = text(listing.Postcode);
  const title = readTitle(html)?.match(TITLE_RE);
  const state =
    title && title[2].trim().toLowerCase() === suburb?.toLowerCase() ? stateCode(title[3]) : null;

  const sold = text(listing.Availability)?.toLowerCase() === "sold";
  const hit: Record<string, unknown> = {
    address: formatAddress(streetLine, suburb, state, postcode),
    suburb,
    state,
    postcode,
    listingType: sold ? "sold" : null,
    saleDate: sold ? parseListingDate(text(listing.SoldDate)) : null,
  };

  const soldPrice = listing.SoldPrice;
  hit.salePrice =
    sold && listing.HidePrice !== true && typeof soldPrice === "number" && soldPrice >= 10_000
      ? soldPrice
      : null;
  hit.price = hit.salePrice;
  // The sold line and, when disclosed, the amount under it:
  //   <p class="f-bold mb-2"> Sold on Nov 16, 2015</p> <p class="f-light mb-0"><span>$1,175,000</span>
  const event = html.match(/Sold on [^<]*<\/p>\s*<p[^>]*>\s*<span>([^<]*)<\/span>/i);
  hit.priceDisplay = sold && event ? text(htmlText(event[1])) : null;

  hit.bedrooms = count(listing.Bedrooms);
  hit.bathrooms = count(listing.Bathrooms);
  hit.parking = count(listing.Carports);
  hit.propertyType = propertyTypeLabel(text(listing.PropertyType));

  const land = html.match(/Land Size<\/span>\s*([\d,.]+)\s*(?:sqm|m2|m²)/i);
  hit.landSize = land ? areaValue(land[1]) : null;
  hit.landSizeUnit = hit.landSize ? "sqm" : null;

  const lat = listing.Latitude;
  const lng = listing.Longitude;
  if (typeof lat === "number" && typeof lng === "number" && lat !== 0 && lng !== 0) {
    hit.latitude = lat;
    hit.longitude = lng;
  }

  const agentsStart = html.indexOf('id="propertydetails-agents"');
  const agents = new Set<string>();
  if (agentsStart >= 0) {
    for (const match of html
      .slice(agentsStart, agentsStart + 20_000)
      .matchAll(/data-name="([^"]+)"/g)) {
      const name = htmlText(match[1]);
      if (name) agents.add(name);
    }
  }
  hit.agentName = agents.size > 0 ? [...agents].join(", ") : null;
  hit.agencyName = "Toop & Toop";

  return hit;
}
